import React, { useCallback } from 'react'
import cn from 'classnames'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import { Tag } from 'primereact/tag'
import { Button } from 'primereact/button'

import { ChargePoint, ChargePointStatus } from '../redux/api/web-api'
import { Input } from '../uikit/Input'
import { MAX_NAME_LENGTH } from '../constants/InputsLength'
import { ChargePointStatus as Status } from '../enums/ChargePointStatusEnum'
import useText from '../hooks/useText'

import { IFormValues } from './ChargePointTypeLabel'

export const SDiv = styled.div`
    padding: 0 ${({ theme }) => theme.sizing.padding.m};
`

export const SProtocolSpan = styled.span`
    font-weight: 500;
    color: #7e7e7e;
    margin-top: 2px;
`

export const TypeContainer = styled.div`
    display: flex;

    font-weight: 400;
    font-size: 14px;
    margin-bottom: ${({ theme }) => theme.sizing.margin.m};
    font-family: 'Lateral Extended';
`

export const SEditSaveButton = styled.button`
    background-color: transparent;
    gap: 7px;
    font-size: 16px;
    display: flex;
    border: none;
    color: ${({ theme }) => theme.brandColors.CA_Blue};
    opacity: 1;
    border: none;
    outline: none;
    transition: opacity 0.1s ease-in-out;

    &:hover {
        opacity: 1;
    }
    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }
`

export const SNameInput = styled.input`
    background-color: white;
    padding: 10px 15px;
    font-size: 14px;
    font-weight: 400;
    width: 70%;
    position: relative;
    border: 1px solid #cccccc;
    border-radius: 5px;
    margin-bottom: 25px;
`

export const SNameField = styled.span`
    letter-spacing: 1%;
    font-family: ${({ theme }) => theme.font.name};
    font-family: 'Lateral Extended';
    font-weight: 500;
    font-size: 20px;
    &.userapp {
        font-family: 'Lateral';
        font-weight: 400;
        font-size: 16px;
    }
`

export const SNameFieldInput = styled.input`
    letter-spacing: 1%;
    font-family: ${({ theme }) => theme.font.name};
    font-family: 'Lateral Extended';
    font-weight: 500;
    font-size: 20px;
    border-width: 0 0 1px 0;

    &.error {
        border-color: ${({ theme }) => theme.font.errorColor};
    }
    &.userapp {
        font-family: 'Lateral';
        font-weight: 400;
        font-size: 16px;
    }
`

export const SNameContainer = styled.div`
    display: flex;
    justify-content: space-between;
    flex-flow: column;
    margin-bottom: ${({ theme }) => theme.sizing.margin.s};
`

export const SErrorDiv = styled.div`
    color: ${({ theme }) => theme.font.errorColor};
`

export const SNameWrapper = styled.div`
    display: flex;
    justify-content: space-between;
`

interface IComponentProps {
    editable?: boolean
    name: string | undefined | null
    schema: unknown
    updatedChargePointStatus?: ChargePointStatus
    saveData: (formData: IFormValues) => void
    chargePoint?: ChargePoint
    edit: boolean
    setEdit: React.Dispatch<React.SetStateAction<boolean>>
    title?: string | React.ReactElement
}

const ComponentTypeLabel = ({
    editable = false,
    schema,
    name,
    title,
    saveData,
    edit,
    updatedChargePointStatus,
    chargePoint,
    setEdit,
}: IComponentProps): React.ReactElement => {
    const text = useText()

    const {
        register,
        handleSubmit,
        formState: { errors, isValid, isSubmitting },
    } = useForm<IFormValues>({
        defaultValues: {
            name: name ?? undefined,
        },
        resolver: yupResolver(schema, { abortEarly: false }),
        mode: 'all',
    })

    const getChargePointStatus = (status?: ChargePointStatus) => {
        switch (status) {
            case Status.Online:
                return <Tag severity="success" value={text('lblOnline')}></Tag>
            case Status.Offline:
                return <Tag severity="danger" value={text('lblOffline')}></Tag>
        }
    }

    const onEdit = useCallback(() => {
        setEdit((edit) => !edit)
    }, [setEdit])

    return (
        <>
            <SNameContainer>
                {edit ? (
                    <>
                        <SNameWrapper>
                            <Input
                                id="name"
                                maxLength={MAX_NAME_LENGTH}
                                type="text"
                                data-testid="name-input"
                                {...register('name')}
                                className={cn({ error: errors.name?.message })}
                            />
                            <div className="ml-2 flex mt-1">
                                <Button
                                    icon="pi pi-times"
                                    outlined
                                    size="small"
                                    severity="danger"
                                    onClick={onEdit}
                                    data-testid="edit-close"
                                    className="pointer text-red-300 p-button-icon-noBorder mr-2"
                                    title={text('btnCancel')}
                                ></Button>
                                <Button
                                    icon="pi pi-check"
                                    size="small"
                                    outlined
                                    severity="success"
                                    onClick={handleSubmit(saveData)}
                                    title={text('btnSave')}
                                    className="pointer text-green-300 p-button-icon-noBorder"
                                    disabled={!isValid || isSubmitting}
                                    data-testid="edit-save"
                                ></Button>
                            </div>
                        </SNameWrapper>
                        {errors.name?.message && <SErrorDiv>{errors.name?.message}</SErrorDiv>}
                    </>
                ) : (
                    <>
                        <div className="flex align-items-center">
                            <div className="font-medium text-xl">{name}</div>
                            {editable && (
                                <Button
                                    onClick={onEdit}
                                    data-testid="edit-button"
                                    className="pointer text-blue-500"
                                    icon="pi pi-pencil"
                                    title={text('lblEdit')}
                                    outlined
                                    text
                                ></Button>
                            )}
                        </div>
                    </>
                )}
            </SNameContainer>
            {chargePoint && (
                <div
                    className="flex text-normal align-items-center gap-1"
                    data-testid={`chargePointType-${chargePoint.id}`}
                >
                    <span>{title}</span>
                    <span>•</span>
                    <div className="font-medium text-500">
                        {chargePoint?.isOcpp ? text('lblOcpp') : text('lblCapiField')}
                    </div>
                    {chargePoint?.meterType === 'CA_MID_01_DE' ? (
                        <>
                            <span className="text-500">•</span>
                            <div className="font-medium text-500">Eichrecht</div>
                        </>
                    ) : null}

                    <div className="ml-1">
                        {updatedChargePointStatus && getChargePointStatus(updatedChargePointStatus)}
                    </div>
                </div>
            )}
        </>
    )
}

export default ComponentTypeLabel
